import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { Wallet, LineChart, PiggyBank, PieChart, CreditCard, HandCoins, ShieldCheck, LifeBuoy, Landmark, LayoutGrid } from "lucide-react";
import api, { inr, getPublicSettings } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CAT_ICON = {
  "Demat Account": LineChart, "Savings Account": PiggyBank, "Mutual Fund": PieChart,
  "EMI Card": Wallet, "Credit Card": CreditCard, "Personal Loan": HandCoins,
  "KYC/CIBIL Based Loan": ShieldCheck, "Insurance": LifeBuoy, "Other": LayoutGrid,
};

function earnLabel(camps) {
  const pct = camps.find((c) => c.payout_type === "percent" && c.payout_percent);
  if (pct) return `${pct.payout_percent}%`;
  const mx = Math.max(0, ...camps.map((c) => Number(c.payout_pool) || 0),
    ...camps.flatMap((c) => (c.payout_slabs || []).map((s) => Number(s.payout) || 0)));
  return mx ? inr(mx) : "—";
}

export default function Overview() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [cats, setCats] = useState([]);
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/wallet/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/referral/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    getPublicSettings().then((d) => { if (d.product_categories?.length) setCats(d.product_categories); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!embla) return;
    const onSel = () => setIdx(embla.selectedScrollSnap());
    embla.on("select", onSel);
    const t = setInterval(() => embla.scrollNext(), 4000);
    return () => clearInterval(t);
  }, [embla]);

  return (
    <div className="space-y-6" data-testid="overview-home">
      {/* greeting */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#0B223D] text-white font-bold flex items-center justify-center text-lg shrink-0">{user?.name?.[0]?.toUpperCase()}</div>
        <h1 className="font-display text-xl font-extrabold text-[#0B223D]">Hi, {user?.name?.split(" ")[0]}</h1>
      </div>

      {/* balance card */}
      <div className="rounded-3xl overflow-hidden shadow-lg shadow-violet-900/15" data-testid="balance-card">
        <div className="bg-gradient-to-br from-violet-700 to-purple-600 text-white p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0"><Wallet className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-white/70">Balance</p>
              <p className="font-mono-num font-extrabold text-xl" data-testid="balance-value">{inr(s?.available || 0)}</p>
            </div>
          </div>
          <Link to="/dashboard/refer"><button className="bg-white text-violet-700 font-bold text-xs px-4 py-2.5 rounded-full shrink-0" data-testid="add-lead-btn">ADD LEAD</button></Link>
        </div>
        <div className="bg-violet-800/95 text-white px-5 py-3 flex items-center justify-between">
          <span className="text-sm text-white/70">Total Earning</span>
          <span className="font-mono-num font-bold" data-testid="total-earning-value">{inr(s?.lifetime || 0)}</span>
        </div>
      </div>

      {/* sliding product banner */}
      {campaigns.length > 0 && (
        <div data-testid="home-banner-carousel">
          <div className="overflow-hidden rounded-3xl border border-violet-100" ref={emblaRef}>
            <div className="flex">
              {campaigns.map((c) => (
                <div key={c.id} className="flex-[0_0_100%] min-w-0 bg-gradient-to-br from-violet-50 to-purple-50 p-6 sm:p-8">
                  <h3 className="font-display font-extrabold text-2xl text-[#0B223D]">{c.name.replace(" Campaign", "")}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2 max-w-md">{c.description}</p>
                  <div className="mt-4 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-400">Earn upto</p>
                      <p className="font-mono-num font-extrabold text-4xl text-violet-700">{earnLabel([c])}</p>
                    </div>
                    <Link to="/dashboard/refer"><button className="bg-violet-700 hover:bg-violet-800 text-white font-bold text-sm px-6 py-3 rounded-full transition-colors" data-testid={`banner-refer-${c.id}`}>REFER NOW</button></Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-1.5 mt-3">
            {campaigns.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-violet-600" : "w-1.5 bg-slate-300"}`} />)}
          </div>
        </div>
      )}

      {/* sell & earn grid */}
      {cats.length > 0 && (
        <div>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <h2 className="font-display font-extrabold text-sm tracking-widest text-[#0B223D]">SELL &amp; EARN</h2>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-4">
            {cats.map((name) => {
              const Icon = CAT_ICON[name] || Landmark;
              const cc = campaigns.filter((c) => (c.category || "") === name);
              return (
                <button key={name} onClick={() => nav(`/dashboard/refer?cat=${encodeURIComponent(name)}`)} data-testid={`ov-cat-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden text-left hover:shadow-md transition-shadow">
                  <div className="p-3 pb-1.5 flex items-start justify-between gap-1">
                    <p className="font-bold text-xs sm:text-sm text-[#0B223D] leading-tight">{name}</p>
                    <Icon className="w-6 h-6 shrink-0 text-violet-600" />
                  </div>
                  <p className="px-3 text-[10px] text-slate-400">Earn upto</p>
                  <div className="mt-1 text-center font-mono-num font-bold text-white bg-violet-600 py-1.5 text-xs sm:text-sm">{earnLabel(cc)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* additional earning options */}
      <div>
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <h2 className="font-display font-extrabold text-sm tracking-widest text-[#0B223D]">ADDITIONAL EARNING OPTIONS</h2>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <div className="mt-4 max-w-xs">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="font-bold text-sm text-[#0B223D]">Mutual Fund<br />Distribution</p>
                <PieChart className="w-6 h-6 text-violet-600 shrink-0" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Earn Recurring Commissions</p>
            </div>
            <Link to="/dashboard/refer?cat=Mutual%20Fund"><button className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs py-2.5 transition-colors" data-testid="ov-mf-start">Start Now</button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
